'use client';

import { formatDate } from '@wolfejs/core/utils/date';
import { FormatCurrency } from '@wolfejs/ui/Format';
import { Table } from '@wolfejs/ui/Table';
import type { TableColumnDef } from '@wolfejs/ui/Table/types';
import { Title } from '@wolfejs/ui/Title';
import { cn } from '@wolfejs/ui/utils/classnames';
import { BarChartView, LineChartView } from '../Charts';
import { DashboardPanel } from '../DashboardPanel';
import type { MappedOrder, ReportProps } from './types';

export const columns: TableColumnDef<MappedOrder>[] = [
  {
    accessorFn: row => row.orderId,
    id: 'orderId',
    header: 'Order Id',
    cell: info => <span>{info.getValue() as string}</span>,
  },
  {
    accessorFn: row => row.creationDate,
    id: 'date',
    header: 'Date',
    cell: info => <span>{formatDate(info.getValue() as Date)}</span>,
  },
  {
    accessorFn: row => row.total,
    id: 'total',
    header: 'Total',
    cell: info => (
      <FormatCurrency
        className="text-sm font-semibold"
        numberFormatBaseProps={{ value: (info.getValue() as number) / 100, displayType: 'text' }}
      />
    ),
  },
  {
    accessorFn: row => row.status,
    id: 'status',
    header: 'Status',
    cell: info => <span>{info.getValue() as string}</span>,
  },
  {
    accessorFn: row => row.noOfItems,
    id: 'noOfItems',
    header: '# of items',
    cell: info => <span className="text-sm font-semibold">{info.getValue() as string}</span>,
  },
];
export const Report = ({
  data,
  dataKey,
  type,
  chartType,
  showTotal = true,
  totalField = 'total',
  showTotalAs = 'currency',
  bars,
}: ReportProps) => {
  return (
    <div className={cn('@container container mt-4')}>
      <div>
        {(showTotal || chartType !== 'none') && data && data.orders?.length > 0 ? (
          <DashboardPanel>
            {showTotal && data.orders.length ? (
              <Title as="h3">
                {showTotalAs == 'currency' && (
                  <FormatCurrency
                    useHumanReadable
                    prefix="$"
                    numberFormatBaseProps={{
                      value: data[totalField] ? (data[totalField] as number) / 100 : 0,
                      displayType: 'text',
                    }}
                  />
                )}
                {showTotalAs == 'quantity' && <span>{data[totalField]}</span>}
              </Title>
            ) : null}
            {chartType === 'linear' && dataKey ? <LineChartView data={data.orders} dataKey={dataKey} /> : null}
            {chartType === 'bar' && dataKey ? (
              <BarChartView
                xDataKey={type === 'cards-summary' ? 'name' : 'brandName'}
                bars={bars || []}
                data={
                  type === 'cards-summary'
                    ? [
                        { total: data.digitalCards, name: 'Digital' },
                        { total: data.plasticCards, name: 'Plastic' },
                      ]
                    : data?.brands || []
                }
              />
            ) : null}
          </DashboardPanel>
        ) : null}
        <div className="mt-4">
          <DashboardPanel>
            <Title as="h3">Report Data</Title>
            <Table<MappedOrder>
              fullWidth
              data={data.orders}
              columns={columns}
              classNames={{ thead: 'bg-default-50 dark:bg-default-800 uppercase', th: 'text-default-500 text-xs' }}
            />
          </DashboardPanel>
        </div>
      </div>
    </div>
  );
};
